import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import ReceptionSettingsErrorModal from "~/components/modal/ReceptionSettingsErrorModal";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import {
  saveActivationData,
  getActivationData,
  fetchAndSavePhoneBooksEntity,
  initAppSettingAutoUpdateData,
  initPhoneBookAutoUpdateData,
  saveAppSettingsData,
  clearPhoneBooksData,
  clearAppSettingsData,
  clearAllReceptionData,
  type ActivationData,
  type ActivationCheckResponse,
  type PhoneBooksData,
  type AppSettingsResponse,
} from "~/utils/receptionDB";
import { convertStringToDate } from "~/utils/dateTimeUtils";
import {
  resolveAutoUpdateTimesFromCheck,
  syncActivationDataFromCheck,
} from "~/utils/receptionUtils";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import "~/css/SetActivation.css";
import "~/css/Button.css";

const activationUrl = buildIfaceApiUrl("v1/activation");
const activationCheckUrl = buildIfaceApiUrl("v1/activation/check");
const phoneBooksUrl = buildIfaceApiUrl("v1/phone-books");
const appSettingsUrl = buildIfaceApiUrl("v1/app-settings");

const getReceptionUuid = (): string => {
  const stored = localStorage.getItem("receptionUuid");
  if (stored) {
    return stored;
  }

  const newId = uuidv4();
  localStorage.setItem("receptionUuid", newId);
  return newId;
};

const formatExpireDate = (value?: string) => {
  if (!value) return "";
  return value.replace(/-/g, "/");
};

// API エラーから errorCode を数値として取り出す（取得できない場合は null）
const extractErrorCode = (error: unknown): number | null => {
  const rawCode = (error as { response?: { data?: { errorCode?: number | string } } })?.response
    ?.data?.errorCode;
  if (typeof rawCode === "number") return rawCode;
  return Number.isFinite(Number(rawCode)) ? Number(rawCode) : null;
};

const ActivationInputForm: React.FC<{
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  inputType?: "text" | "password";
}> = ({ label, value, error, onChange, inputType = "text" }) => {
  const errorText = error ?? "\u00A0";
  return (
    <div className="activation-field">
      <div className="activation-row">
        <span className="activation-label">{label}</span>
        <div className="activation-input-wrapper">
          <input
            className="activation-input"
            type={inputType}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
      <div className={`activation-error-row ${error ? "" : "is-hidden"}`}>
        <span className="activation-error-label-spacer" aria-hidden="true"></span>
        <div className="activation-error-text">{errorText}</div>
      </div>
    </div>
  );
};

/**
 * デバイスのアクティベーション情報を入力・送信するフォームコンポーネント。
 *
 * - フォーム状態: `deviceName`, `activationId`, `activationCode` を内部状態として保持し、入力値の必須チェックを行う。
 * - 使用 API: `activationUrl`(`REACT_APP_IFACE_PUBLIC_API_URL` を基に組み立てられた `.../v1/activation`) に対して `apiClient` でアクティベーション API を呼び出す。
 * - 成功時 UI: アクティベーションが成功した場合は、バリデーションエラーをクリアし、ローディング状態を解除する（必要に応じて呼び出し元画面での遷移などを行うことを想定）。
 * - 失敗時 UI: アクティベーション API がエラーを返した場合は `errorCode` を内部状態に保持し、`ReceptionSettingsErrorModal` を表示してエラー内容をユーザーに通知する。
 *
 * 主要な副作用:
 * - `getReceptionUuid` を通じて `localStorage` に `"receptionUuid"` を保存/参照し、同一ブラウザでのアクティベーション要求を識別する。
 * - `getActivationData` を通じて IndexedDB(受付用 DB) に保存された `"activationExpireDate"` を参照し、期限表示や有効期限有無(`hasExpireDate`)の制御に利用する。
 */
const SetActivation: React.FC = () => {
  const { t } = useTranslation();
  const uuid = useMemo(() => getReceptionUuid(), []);
  const [deviceName, setDeviceName] = useState("");
  const [activationId, setActivationId] = useState("");
  const [activationCode, setActivationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasExpireDate, setHasExpireDate] = useState(false);
  const [expireDate, setExpireDate] = useState("");
  const [isActivationActive, setIsActivationActive] = useState(true);
  const [savedDeviceName, setSavedDeviceName] = useState("");

  const updateExpireDateStatus = (data: ActivationData) => {
    setExpireDate(formatExpireDate(data.expireDate));
    setHasExpireDate(true);

    const parsedExpireDate = convertStringToDate(data.expireDate, data.timezone, "yyyy-MM-dd");
    if (!parsedExpireDate) {
      setIsActivationActive(false);
      return;
    }

    // 要件に合わせ、expireDate >= now の場合に有効(アクティブ)として扱う
    setIsActivationActive(parsedExpireDate.getTime() >= Date.now());
  };

  // マウント時に activation/check を呼び出し、maxUser / expireDate の差分を反映する。
  // 未登録（エラーコード102）の場合は全データを削除する。
  const runActivationCheck = async () => {
    try {
      const checkRes = await apiClient.get<{ data: ActivationCheckResponse }>(activationCheckUrl, {
        headers: createIfaceHeaders(true),
      });
      const activationCheckData = checkRes?.data?.data;

      // activation/check の結果と IndexedDB の値を比較し、差分があれば更新する。
      // 更新があれば有効期限表示も再反映する。
      const updatedActivation = await syncActivationDataFromCheck(activationCheckData);
      if (updatedActivation?.expireDate && updatedActivation.timezone) {
        updateExpireDateStatus(updatedActivation);
      }
    } catch (error) {
      if (extractErrorCode(error) === 102) {
        await clearAllReceptionData();
        setHasExpireDate(false);
        setExpireDate("");
        setSavedDeviceName("");
        window.dispatchEvent(new CustomEvent("reception:activationDataCleared"));
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      const data = await getActivationData();
      if (data) {
        if (data.expireDate && data.timezone) {
          updateExpireDateStatus(data);
        }
        // IndexedDB に保存された deviceName を表示に使う
        if (data.deviceName) {
          setSavedDeviceName(data.deviceName);
        }
      }

      // activation/check は保存済みデータの有無に関わらず呼び出す
      // （未登録=102 の検知や差分反映を常に行うため）。
      await runActivationCheck();
    };

    init();
  }, []);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    deviceName?: string;
    activationId?: string;
    activationCode?: string;
  }>({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validate = () => {
    const nextErrors: {
      deviceName?: string;
      activationId?: string;
      activationCode?: string;
    } = {};

    if (!deviceName.trim()) {
      nextErrors.deviceName = t("activationDeviceNameRequired");
    }
    if (!activationId.trim()) {
      nextErrors.activationId = t("activationIdRequired");
    }
    if (!activationCode.trim()) {
      nextErrors.activationCode = t("activationCodeRequired");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ダウンロード処理を行い、成功したかどうかを boolean で返す
  const downloadPhoneBooksAndAppSettings = async (): Promise<boolean> => {
    try {
      await clearPhoneBooksData();
      await clearAppSettingsData();

      const headers = createIfaceHeaders(true);

      const [phoneBooksResponse, appSettingsResponse] = await Promise.all([
        apiClient.get<{ data: PhoneBooksData }>(phoneBooksUrl, { headers }),
        apiClient.get<AppSettingsResponse>(appSettingsUrl, { headers }),
      ]);

      const phoneBooksData = phoneBooksResponse.data.data as PhoneBooksData;
      const responseDataUrl = phoneBooksData.responseDataUrl;
      await fetchAndSavePhoneBooksEntity(responseDataUrl, phoneBooksData);
      await saveAppSettingsData(appSettingsResponse.data as AppSettingsResponse);

      return true;
    } catch (error) {
      // ダウンロード/保存が失敗した場合は false を返す（例外は握りつぶす）
      console.error("Failed to download phonebooks/app settings:", error);
      return false;
    }
  };

  // activation 成功データにデバイス名を含めて IndexedDB に保存し、表示へ反映する
  const persistActivationData = async (apiData: ActivationData) => {
    const apiToSave: ActivationData = {
      ...apiData,
      deviceName: deviceName,
    };
    try {
      await saveActivationData(apiToSave);
    } catch (e) {
      console.error("Failed to save activation data to IndexedDB:", e);
    }
    updateExpireDateStatus(apiToSave);
    if (apiToSave.deviceName) setSavedDeviceName(apiToSave.deviceName);
  };

  // 初期化: activation による初回の auto-update 値を設定する（lastUpdateDate を null にする）
  const initAutoUpdateData = async (apiData: ActivationData) => {
    try {
      const headers = createIfaceHeaders(true);
      const checkRes = await apiClient.get<{ data: ActivationCheckResponse }>(activationCheckUrl, {
        headers,
      });
      const activationCheckData = checkRes?.data?.data;

      const storedActivation = await getActivationData();
      const timezone = apiData.timezone ?? storedActivation?.timezone;

      const { appSettingUpdateDateTime: formattedApp, phoneBookStartDateTime: formattedPhone } =
        resolveAutoUpdateTimesFromCheck(activationCheckData, timezone);

      await initAppSettingAutoUpdateData(formattedApp);
      await initPhoneBookAutoUpdateData(formattedPhone);
    } catch (err) {
      console.error("Failed to init auto-update data for new activation:", err);
    }
  };

  // フォーム入力とエラー表示をクリアする
  const clearForm = () => {
    setDeviceName("");
    setActivationId("");
    setActivationCode("");
    setErrors({});
    setErrorCode(null);
  };

  const applySuccess = async (apiData?: ActivationData) => {
    if (apiData) {
      await persistActivationData(apiData);
      await initAutoUpdateData(apiData);
    }
    clearForm();

    const downloadSuccess = await downloadPhoneBooksAndAppSettings();
    // 通信・保存が成功したか否かを detail.success で伝える（リスナー側が完了/失敗を判別できるようにする）
    window.dispatchEvent(
      new CustomEvent("reception:phonebooksAndAppSettingsDownloaded", {
        detail: { success: downloadSuccess },
      })
    );
    // アクティベーション成功を確認するモーダルを表示する（文言はi18n）
    setSuccessMessage(t("messageSuccessModal"));
    setShowSuccessModal(true);
  };

  const applyError = (error: unknown) => {
    const code =
      (error as { response?: { data?: { errorCode?: number | string } } })?.response?.data
        ?.errorCode ?? "unknown";
    setErrorCode(String(code));
    setShowErrorModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post<{ data?: ActivationData }>(
        activationUrl,
        {
          name: deviceName,
          authenticationKey: activationId,
          authenticationCode: activationCode,
          uuid,
        },
        {
          headers: createIfaceHeaders(),
        }
      );

      const apiData = response?.data?.data as ActivationData | undefined;
      await applySuccess(apiData);
    } catch (error) {
      applyError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="activation-settings">
      <div className="activation-content">
        <div className="activation-success">
          {hasExpireDate
            ? isActivationActive
              ? t("activationSuccessMessage", { date: expireDate, deviceName: savedDeviceName })
              : t("activationExpiredMessage", { date: expireDate, deviceName: savedDeviceName })
            : "\u00A0"}
        </div>

        <form onSubmit={handleSubmit} className="activation-form">
          <ActivationInputForm
            label={t("activationDeviceName")}
            value={deviceName}
            error={errors.deviceName}
            onChange={setDeviceName}
          />
          <ActivationInputForm
            label={t("activationId")}
            value={activationId}
            error={errors.activationId}
            onChange={setActivationId}
          />
          <ActivationInputForm
            label={t("activationCode")}
            value={activationCode}
            error={errors.activationCode}
            onChange={setActivationCode}
            inputType="password"
          />

          <div className="activation-button-container">
            <button type="submit" className="button-1 flexible" disabled={isLoading}>
              {isLoading ? t("activationVerifying") : t("activationVerify")}
            </button>
          </div>
        </form>
      </div>

      <ReceptionSettingsErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t("error")}
        body={t(`ifaceErrorCode.activation.${errorCode ?? "unknown"}`)}
        confirmText={t("close")}
      />
      <ConfirmationModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage(null);
        }}
        handleConfirm={() => {
          setShowSuccessModal(false);
          setSuccessMessage(null);
        }}
        title={t("titleSuccessModal")}
        body={successMessage ?? ""}
        cancelText={""}
        confirmText={t("ok")}
      />
      {isLoading && <ReceptionCommunicatingIndicator />}
    </div>
  );
};

export default SetActivation;
